/**
 * Detail myšlienky `/ideas/:id` (F4AI-19, S13) — `GET /ideas/:id`
 * (`IdeaRow` + `suggestions[]`).
 *
 * Obsah: status pill + rating ★ badge + accepted pill, popis, topics,
 * timeline `status_history` (všetky čitateľné 10030 AUTORA, created_at
 * ASC — kanonická stavová línia), seznam `suggestions` (LWW 10030 od
 * iných pubkey — NIE status, len návrhy, rozhodnutie manažéra S10).
 *
 * ── Claim flow (F1) ─────────────────────────────────────────────────────
 *  - **Claim** tlačidlo má ktokoľvek: podpíše 10030 draft s `d` = id
 *    idey a `a` = vlastný pubkey (claimant), content JSON
 *    `{"status":"claimed"}` a pošle `POST /events`. Pre autora je to
 *    kanonický status, pre cudzích je to **návrh** (suggestion).
 *  - **Legálnosť** kontrolujeme client-side kópiou automatu z
 *    `gateway/src/ideas.ts` (`lib/ideas.ts` `canTransition`): nelegálny
 *    prechod → disabled tlačidlo s `title` tooltipom (dôvod). Gateway
 *    by event prijal (LWW per publikujúci), ale UI nesmie lákať na
 *    zmätenú stavovú líniu.
 *  - **Autor** vidí navyše „potvrdit claim“: publishne vlastný 10030
 *    `claimed` s `a` tagom na claimanta posledného `claimed` návrhu —
 *    tým sa z návrhu stane kanonický status (rozhodnutie S10).
 *  - Po publishi `reload()` — invalidate + refetch detailu.
 */
import { useState, type JSX } from 'react';
import { Link, useParams } from 'react-router';

import { useApi } from '../api/ApiContext.js';
import { ApiError } from '../api/client.js';
import { useResource } from '../hooks/useResource.js';
import { useWriteStatus } from '../hooks/useWriteStatus.js';
import { useIdentity } from '../nostr/IdentityContext.js';
import { useSigner } from '../nostr/useSigner.js';
import { resolveSigner } from '../nostr/signer.js';
import { StateError, StateLoading } from '../components/StateView.js';
import { formatAbsoluteTime, formatRelativeTime } from '../lib/format.js';
import { npubShort } from '../lib/npub.js';
import {
  IDEA_STATUS_LABELS,
  buildClaimTemplate,
  canTransition,
  latestClaimSuggestion,
  ratingBadgeText,
} from '../lib/ideas.js';
import type { IdeaStatusEntry } from '../api/types.js';

function StatusEntryRow({ entry, label }: { entry: IdeaStatusEntry; label: string }): JSX.Element {
  return (
    <li data-testid="timeline-entry">
      <span className={`status-pill ${entry.status}`}>{IDEA_STATUS_LABELS[entry.status]}</span>{' '}
      <time dateTime={new Date(entry.created_at * 1000).toISOString()} title={formatAbsoluteTime(entry.created_at)}>
        {formatRelativeTime(entry.created_at)}
      </time>{' '}
      · {label}{' '}
      <Link className="pubkey-chip" to={`/agents/${entry.publisher}`} title={entry.publisher}>
        {npubShort(entry.publisher, 6, 4)}
      </Link>
      {entry.claimant !== null && (
        <>
          {' '}
          → claim{' '}
          <Link className="pubkey-chip" to={`/agents/${entry.claimant}`} title={entry.claimant}>
            {npubShort(entry.claimant, 6, 4)}
          </Link>
        </>
      )}
      {entry.note !== null && <span className="timeline-note"> — “{entry.note}”</span>}
    </li>
  );
}

export function IdeaDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const api = useApi();
  const { identity } = useIdentity();
  // D1/D5: claimant a kontrola autorstva idú zo SKUTOČNÉHO signera
  // (NIP-07 má prednosť pred nsec identitou), nie z `identity.pubkey`
  const { signer } = useSigner();
  const actorPubkey = signer?.pubkey ?? identity?.pubkey ?? null;
  const detail = useResource(() => api.idea(id), [api, id]);
  // B10: claim/status sú zápisy (10030) → zamknuté pre neregistrovaných
  const write = useWriteStatus();
  const lockTitle = write.locked ? `písanie zamknuté: ${write.reason ?? 'write status unavailable'}` : null;

  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** postrieka claim draft (s `a` tagom na claimanta) a publishne ho */
  async function publishClaim(claimant: string, noteText: string): Promise<void> {
    if (identity === null) {
      setError('identita nie je dostupná — zahoď kľúč a skús ho vygenerovať znova');
      return;
    }
    if (write.locked) {
      setError(`písanie zamietnuté: ${write.reason ?? 'write status unavailable'}`);
      return;
    }
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const template = buildClaimTemplate({
        ideaId: id,
        claimant,
        note: noteText,
        createdAt: Math.floor(Date.now() / 1000),
      });
      const signer = await resolveSigner({ secretKey: identity.secretKey });
      const event = await signer.sign(template);
      const result = await api.publish(event);
      if (result.quorum_met) {
        setNote(`claim publikovaný (event ${event.id.slice(0, 12)}…), invalidujem a refetchujem`);
        detail.reload();
      } else {
        setError(result.error ?? 'kvórum relayov nesplnené');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.reasons.join('; ') : err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <nav className="breadcrumbs" aria-label="breadcrumbs">
        <Link to="/ideas">ideas</Link>
        <span>/</span>
        <span className="hex">{`${id.slice(0, 12)}…`}</span>
      </nav>

      {detail.loading && <StateLoading label="načítavam detail myšlienky" />}
      {detail.error !== null && <StateError error={detail.error} onRetry={detail.reload} />}

      {detail.data !== null && (
        <>
          <div className="page-head">
            <div>
              <h1 className="page-title">{detail.data.title}</h1>
              <p className="page-subtitle">
                kind 1021 · autor{' '}
                <Link className="pubkey-chip" to={`/agents/${detail.data.author}`} title={detail.data.author}>
                  {npubShort(detail.data.author)}
                </Link>{' '}
                · <time dateTime={new Date(detail.data.created_at * 1000).toISOString()}>
                  {formatRelativeTime(detail.data.created_at)}
                </time>
              </p>
            </div>
            <div className="page-head-badges" data-testid="detail-badges">
              <span className={`status-pill ${detail.data.status}`} data-testid="detail-status-pill">
                {IDEA_STATUS_LABELS[detail.data.status]}
              </span>
              {(() => {
                const badge = ratingBadgeText(detail.data.rating);
                return badge !== null ? (
                  <span className="rating-badge" data-testid="rating-badge">
                    {badge}
                  </span>
                ) : null;
              })()}
              {detail.data.rating !== null && detail.data.rating.accepted && (
                <span className="accepted-pill" data-testid="accepted-pill">
                  accepted
                </span>
              )}
            </div>
          </div>

          <section className="glass-card" data-testid="idea-detail-body">
            <p className="event-body">{detail.data.description === '' ? '(bez popisu)' : detail.data.description}</p>
            {detail.data.topics.length > 0 && (
              <div className="event-meta">
                {detail.data.topics.map((topic) => (
                  <Link key={topic} className="tag-chip" to={`/ideas?topic=${encodeURIComponent(topic)}`}>
                    #{topic}
                  </Link>
                ))}
              </div>
            )}
            {detail.data.claimant !== null && (
              <p className="claimant-chip" data-testid="detail-claimant">
                kanonický claimant:{' '}
                <Link className="pubkey-chip" to={`/agents/${detail.data.claimant}`} title={detail.data.claimant}>
                  {npubShort(detail.data.claimant)}
                </Link>
              </p>
            )}
            {detail.data.rating !== null && (
              <p className="claim-hint" data-testid="rating-aggregate">
                rating: {detail.data.rating.count}× priemer {detail.data.rating.mean15.toFixed(2)}/5 · n_eff{' '}
                {detail.data.rating.n_eff.toFixed(2)} · wilson {detail.data.rating.wilson.toFixed(2)} ·{' '}
                {detail.data.rating.accepted_count > 0 ? 'akceptované riešenie' : 'bez akceptácie'}
              </p>
            )}
          </section>

          <section className="glass-card" data-testid="claim-section">
            <p className="side-title">claim</p>
            {write.locked && (
              <p className="notice" data-testid="claim-locked" title={lockTitle ?? undefined}>
                {write.reason ?? 'write status unavailable'} — claim píšu len registrovaní operátori
              </p>
            )}
            {(() => {
              const check = canTransition(detail.data.status, 'claimed');
              return (
                <div className="claim-actions">
                  <button
                    type="button"
                    className="primary-btn"
                    data-testid="claim-btn"
                    disabled={!check.ok || busy || write.locked}
                    title={lockTitle ?? (check.ok ? 'podepíše 10030 claimed s a-tagom na tvoj pubkey' : (check.reason ?? ''))}
                    onClick={() => {
                      if (actorPubkey === null) {
                        void publishClaim('unknown', 'claim z UI (F4AI-19)');
                        return;
                      }
                      void publishClaim(actorPubkey, 'claim z UI (F4AI-19)');
                    }}
                  >
                    Claim
                  </button>
                  {!check.ok && (
                    <span className="claim-hint" data-testid="claim-disabled-reason">
                      {check.reason}
                    </span>
                  )}
                </div>
              );
            })()}

            {actorPubkey !== null && actorPubkey === detail.data.author && (
              (() => {
                const confirmTarget = latestClaimSuggestion(detail.data.suggestions);
                const check = canTransition(detail.data.status, 'claimed');
                return (
                  <div className="claim-actions" data-testid="author-confirm">
                    <button
                      type="button"
                      className="ghost-btn"
                      data-testid="confirm-claim-btn"
                      disabled={confirmTarget === null || !check.ok || busy || write.locked}
                      title={
                        lockTitle ??
                        (confirmTarget === null
                          ? 'zatial ziadny claimed navrh, ktory by sa dal potvrdit'
                          : check.ok
                            ? `publishne kanonicky 10030 claimed s a-tagom na ${confirmTarget.slice(0, 12)}…`
                            : (check.reason ?? ''))
                      }
                      onClick={() => {
                        if (confirmTarget !== null) void publishClaim(confirmTarget, 'potvrdeny claim od agenta');
                      }}
                    >
                      potvrdit claim
                    </button>
                    {confirmTarget !== null && (
                      <span className="claim-hint">
                        claimant návrhu:{' '}
                        <Link className="pubkey-chip" to={`/agents/${confirmTarget}`} title={confirmTarget}>
                          {npubShort(confirmTarget, 6, 4)}
                        </Link>
                      </span>
                    )}
                  </div>
                );
              })()
            )}

            {note !== null && (
              <p className="notice" data-testid="claim-note">
                {note}
              </p>
            )}
            {error !== null && (
              <p className="notice error" role="alert" data-testid="claim-error">
                {error}
              </p>
            )}
          </section>

          <section className="glass-card" data-testid="status-history">
            <p className="side-title">status history (10030 autora)</p>
            {detail.data.status_history.length === 0 ? (
              <p className="claim-hint">autor zatiaľ nepublikoval žiadny 10030 — status je implicitný `open`</p>
            ) : (
              <ul className="status-timeline">
                {detail.data.status_history.map((entry) => (
                  <StatusEntryRow key={entry.id} entry={entry} label="publikoval" />
                ))}
              </ul>
            )}
          </section>

          <section className="glass-card" data-testid="suggestions-section">
            <p className="side-title">návrhy ({String(detail.data.suggestions.length)})</p>
            <p className="claim-hint">
              10030 od iných pubkey — LWW víťazi per publikujúci, nie kanonický status (rozhodnutie S10)
            </p>
            {detail.data.suggestions.length === 0 ? (
              <p className="claim-hint">nikto zatiaľ nenavrhoval zmenu stavu</p>
            ) : (
              <ul className="status-timeline">
                {detail.data.suggestions.map((entry) => (
                  <StatusEntryRow key={entry.id} entry={entry} label="návrh od" />
                ))}
              </ul>
            )}
          </section>

          <p className="untrusted-note">{detail.data.untrusted}</p>
        </>
      )}
    </>
  );
}
